// frontend/src/components/homepage/artefacts/AiDraft.js
//
// Tile 06's artefact: the AI-drafted reply bubble with the typewriter. A
// literal port of the prototype's `.art-draft .bub` markup and its "draft
// typewriter (multi-line)" script block: one character every 42ms, a 2.6s hold
// on the full text, a 500ms pause on the empty bubble, then again.
//
// Under motion-off or `window.__PRERENDER__` the FULL text renders at once and
// no timer is ever armed - a visitor who asked for no motion reads the message,
// the crawler snapshot carries it. The caret stays in the DOM in both cases:
// its blink is a CSS animation the motion-off block already kills, so it is a
// static bar, exactly the prototype's reduced-motion state.
//
// The loop is gated on the viewport (`useInViewGate`) and on `paused` (tab
// hidden); the character index lives in a ref so a suspended loop resumes
// where it stopped rather than restarting the sentence.
//
// `text` (optional) replaces the drafted message. A profession page passes a
// reply in its own trade; the homepage passes nothing and types DRAFT_TEXT.

import React, { memo, useEffect, useRef, useState } from "react";
import useMotion from "../../../motion/useMotion";
import useInViewGate, { isPrerendering } from "../../../motion/useInViewGate";
import useHydrated from "../../../motion/useHydrated";

export const DRAFT_LABEL = "AI DRAFT · מוכן לשליחה";
export const DRAFT_TEXT =
  "היי, ראיתי שאת מחפשת קוסמטיקאית באזור רעננה - אשמח לעזור, יש לי ניסיון של 12 שנה...";

export const TYPE_MS = 42;
export const HOLD_MS = 2600;
export const RESTART_MS = 500;

function AiDraft({ text = DRAFT_TEXT }) {
  const { off, paused } = useMotion();
  const [gateRef, inView] = useInViewGate();

  const showFull = off || isPrerendering();
  // The prerendered snapshot carries the full draft; the hydrating client's
  // first render must match it (the typing starts from 0 once in view).
  const hydrated = useHydrated();
  const [shown, setShown] = useState(showFull || !hydrated ? text.length : 0);
  const indexRef = useRef(0);

  useEffect(() => {
    if (showFull) {
      setShown(text.length);
      return undefined;
    }
    if (!inView || paused) return undefined;

    let timer = 0;
    let i = indexRef.current;
    const step = () => {
      if (i <= text.length) {
        indexRef.current = i;
        setShown(i);
        i += 1;
        timer = setTimeout(step, TYPE_MS);
      } else {
        timer = setTimeout(() => {
          i = 0;
          indexRef.current = 0;
          setShown(0);
          timer = setTimeout(step, RESTART_MS);
        }, HOLD_MS);
      }
    };
    step();
    return () => clearTimeout(timer);
  }, [showFull, inView, paused, text]);

  return (
    <div ref={gateRef} className="hl-ft-bub" aria-hidden="true">
      <div className="hl-ft-lab">{DRAFT_LABEL}</div>
      <div className="hl-ft-ty">
        {text.slice(0, shown)}
        <i className="hl-ft-caret" />
      </div>
    </div>
  );
}

export default memo(AiDraft);
